import React, { useEffect, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { XCloseIcon } from '@rsos/assets/icons';
import { Text } from '@rsos/components/capstone/base';
import { CloseButton, Result, Results, Wrapper } from './AddressLocator.styles';
import { StyledInput } from './AviveSearchForm.styles';
import { createPlaceholder } from './fixtures/avive-request-spatial';
import useDebouncedValue from './useDebouncedValue';
import usePermissionedGeocoder from './usePermissionedGeocoder';

const AddressLocator = ({
  initialValue = '',
  initialCoordinate = [],
  disabled = false,
}) => {
  const [inputText, setInputText] = useState('');
  const [selected, setSelected] = useState(null);
  const debouncedAddress = useDebouncedValue(inputText);
  const results = usePermissionedGeocoder(debouncedAddress, selected !== null);

  const {
    setFieldValue,
    setFieldTouched,
    values,
    errors,
    touched,
  } = useFormikContext();

  const inputRef = useRef();
  const resultsRef = useRef([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  useEffect(() => {
    if (!values.latitude && !values.longitude) {
      setFieldTouched('latitude', false);
      setFieldTouched('longitude', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (results && results.length > 0 && results[highlightedIndex]) {
      const highlightedResult = results[highlightedIndex];
      const placeholder = createPlaceholder({
        key: 'avive_aed_highlight',
        latitude: highlightedResult.location.y,
        longitude: highlightedResult.location.x,
        transparent: true,
      });
      window.__RSOS?.hermes?.handle(placeholder);
    } else {
      const placeholder = createPlaceholder({
        key: 'avive_aed_highlight',
        display: false,
      });
      window.__RSOS?.hermes?.handle(placeholder);
    }
  }, [highlightedIndex, results]);

  useEffect(() => {
    if (selected) {
      const placeholder = createPlaceholder({
        latitude: selected.location.y,
        longitude: selected.location.x,
      });
      window.__RSOS?.hermes?.handle(placeholder);
    } else {
      const placeholder = createPlaceholder({
        display: false,
      });
      window.__RSOS?.hermes?.handle(placeholder);
    }
  }, [selected]);

  useEffect(() => {
    if (
      initialValue &&
      Array.isArray(initialCoordinate) &&
      initialCoordinate.length === 2
    ) {
      setSelected({
        address: initialValue,
        location: {
          x: initialCoordinate[0],
          y: initialCoordinate[1],
        },
      });
    }
  }, [initialValue, initialCoordinate]);

  // Edge case: When the input text changes clear the selection if text is empty
  useEffect(() => {
    if (!inputText) {
      setHighlightedIndex(-1);
      setSelected(null);
    }
  }, [inputText]);

  // When a new selection is made...
  useEffect(() => {
    if (selected) {
      // Although validateOnChange is set to true at the form level, the formik
      // state won't update immediately. This is a bug since setFieldValue is
      // supposed to trigger validation. See the bug report here:
      // https://github.com/jaredpalmer/formik/issues/2059
      // To work around this, we manually trigger validation by setting
      // touched to true after waiting for the formik state to update.
      (async () => {
        await setFieldValue('longitude', selected?.location?.x || null, true);
        setFieldTouched('longitude', true);
        await setFieldValue('latitude', selected?.location?.y || null, true);
        setFieldTouched('latitude', true);
      })();

      const selectedValue = selected?.address || '';
      setInputText(selectedValue);
      inputRef.current.blur(); // unfocus the input once a selection is made
    } else {
      // When the selection is cleared, clear the formik state as well.
      setFieldValue('longitude', null);
      setFieldValue('latitude', null);
    }
  }, [selected, setFieldValue, setFieldTouched, setInputText]);

  const scrollToHighlighted = index => {
    if (resultsRef.current[index]) {
      resultsRef.current[index].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  };

  const handleKeyDown = event => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const nextIndex =
        highlightedIndex >= results.length - 1 ? 0 : highlightedIndex + 1;
      setHighlightedIndex(nextIndex);
      scrollToHighlighted(nextIndex);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const nextIndex =
        highlightedIndex <= 0 ? results.length - 1 : highlightedIndex - 1;
      setHighlightedIndex(nextIndex);
      scrollToHighlighted(nextIndex);
    } else if (event.key === 'Enter') {
      if (results && results[highlightedIndex]) {
        event.preventDefault();
        const selectedResult = results[highlightedIndex];
        setSelected(selectedResult);
      }
    }
  };

  const handleChange = event => {
    const { value = '' } = event?.target;
    if (selected && selected.address !== value) {
      // When there's already a selection and it's edited, clear the selection
      // so a new match is offered based on the new input.
      setSelected(null);
    }

    setInputText(value);
  };

  const handleClick = result => {
    setSelected(result);
  };

  return (
    <>
      <Wrapper>
        <StyledInput
          type="text"
          data-testid="address-field"
          value={inputText}
          ref={inputRef}
          placeholder="Add Address"
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            setFieldTouched('latitude', true);
            setFieldTouched('longitude', true);
          }}
          // it's enough to check for latitude since both are set together
          error={errors['latitude'] && touched['latitude']}
          disabled={disabled}
        />
        {inputText && results && (
          <Results>
            {results.length > 0 ? (
              results.map((result, idx) => (
                <Result
                  key={`${result.address}-${idx}`}
                  highlighted={highlightedIndex === idx}
                  ref={el => (resultsRef.current[idx] = el)}
                  onClick={() => handleClick(result)}
                >
                  <Text size="normal">{result.address}</Text>
                </Result>
              ))
            ) : (
              <Result>
                <Text size="normal">No results found</Text>
              </Result>
            )}
          </Results>
        )}

        {inputText && !selected && (
          <CloseButton onClick={() => setInputText('')}>
            <XCloseIcon />
          </CloseButton>
        )}
      </Wrapper>

      {errors['latitude'] && touched['latitude'] && (
        <Text size="small" pt={2}>
          Select a valid address
        </Text>
      )}
    </>
  );
};

export default AddressLocator;
